import {
  AppBar,
  Grid,
  InputAdornment,
  InputBase,
  inputBaseClasses,
  Paper,
  SelectChangeEvent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Toolbar,
  Typography,
} from '@mui/material';
import usePagination from '@mui/material/usePagination/usePagination';
import { Magnifier } from 'asset';
import { DeleteDialog, HeadTable, Layout, TableCaption } from 'components';
import { drawerWidth } from 'config';
import { getSortItem } from 'helpers';
import { useDateTime } from 'hooks';
import useDebounce from 'hooks/useDebounce';
import { DELETE_DIALOG_TYPE } from 'models';
import {
  AC_ACTIVITY_LOG_LIST_PARAMS,
  ACTIVITY_LOG_ITEM_TYPE,
  ACTIVITY_LOG_ROLE,
} from 'models/activity-logs';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { useAppDispatch, useAppSelector } from 'store/hook';
import { setActivityLogListParams } from 'store/reducers/activity-logs';
import { getActivityLogListAction } from 'store/reducers/activity-logs/actionTypes';
import { headCells } from './data';

export const ActivityLogAC = () => {
  const dispatch = useAppDispatch();
  const redirect = useNavigate();
  const { formatDateTime } = useDateTime();
  const { list } = useAppSelector(
    (state) => state.activityLogReducer.activityLogs
  );
  const { params } = list;

  const [selected, setSelected] = useState<readonly string[]>([]);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [openDeletePopup, setOpenDeletePopup] = useState(false);
  const [activityLogItem, setActivityItem] = useState<ACTIVITY_LOG_ITEM_TYPE>();

  const onSetListParams = (params: AC_ACTIVITY_LOG_LIST_PARAMS) => {
    dispatch(setActivityLogListParams(params));
  };

  useEffect(() => {
    if (params) {
      dispatch(getActivityLogListAction({ data: params }));
    }
  }, [params, dispatch]);

  useEffect(() => {
    setSearchKeyword(params?.search ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // const setOpenDelete = (event: any, data: ACTIVITY_LOG_ITEM_TYPE) => {
  //   event.stopPropagation();
  //   setActivityItem(data);
  //   setOpenDeletePopup(true);
  // };

  const handleChangeRowsPerPage = (event: SelectChangeEvent) => {
    const limit = parseInt(event.target.value, 10);
    onSetListParams({ ...params, page: 1, limit });
  };

  const isSelected = (name: string) => selected.indexOf(name) !== -1;

  const { items } = usePagination({
    count: list.metadata.total_pages,
    page: list.metadata.page,
    onChange(_event, page) {
      onSetListParams({ ...params, page: page });
    },
  });

  const onSetKeywordParams = useDebounce((searchKeyword: string) => {
    const _params = { ...params, page: 1 };
    if (searchKeyword) _params.search = searchKeyword;
    else delete _params.search;
    onSetListParams(_params);
  }, 500);

  const onChangeSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchKeyword(event.target.value);
    onSetKeywordParams(event.target.value);
  };

  const onClosePopup = () => {
    setOpenDeletePopup(false);
    setActivityItem(undefined);
  };

  const onReloadList = () => {
    if (!params) return;
    dispatch(getActivityLogListAction({ data: params }));
  };

  // const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
  //   if (event.target.checked) {
  //     const newSelected = list.records.map((n) => n.id);
  //     setSelected(newSelected);
  //     return;
  //   }
  //   setSelected([]);
  // };

  // const handleClick = (_: any, name: string) => {
  //   const selectedIndex = selected.indexOf(name);
  //   let newSelected: readonly string[] = [];
  //   if (selectedIndex === -1) {
  //     newSelected = newSelected.concat(selected, name);
  //   } else if (selectedIndex === 0) {
  //     newSelected = newSelected.concat(selected.slice(1));
  //   } else if (selectedIndex === selected.length - 1) {
  //     newSelected = newSelected.concat(selected.slice(0, -1));
  //   } else if (selectedIndex > 0) {
  //     newSelected = newSelected.concat(
  //       selected.slice(0, selectedIndex),
  //       selected.slice(selectedIndex + 1)
  //     );
  //   }

  //   setSelected(newSelected);
  // };

  const sortItem = useMemo(() => {
    return getSortItem(list.params?.sort);
  }, [list.params]);

  const handleRequestSort = (
    _: React.MouseEvent<unknown>,
    property: keyof ACTIVITY_LOG_ITEM_TYPE
  ) => {
    setSelected([]);
    const isAsc = sortItem?.orderBy === property && sortItem?.order === 'asc';
    onSetListParams({
      ...params,
      sort: `${property}-${isAsc ? 'desc' : 'asc'}`,
    });
  };

  const redirectDescription = (rowItem: ACTIVITY_LOG_ITEM_TYPE) => {
    if (rowItem.description.includes('SUB-')) {
      redirect(`/concierge/subscription/${rowItem.metadata?.parent_id}`);
    }
    if (
      rowItem.description.includes('JOB-') ||
      rowItem.description.includes('ACJ-') ||
      rowItem.description.includes('ACT-')
    ) {
      redirect(
        `/jobs/${rowItem.metadata?.job_id ?? rowItem.metadata?.parent_id}`
      );
    }
  };

  /**
   * Splits a description into 3 parts: everything before a (SUB|JOB)-\d{6}-\d{6} number, the number itself, and everything after the number.
   * @param {string} description The description to split.
   * @returns {Object} An object with 3 properties: `beforeNumber`, `number`, and `afterNumber`.
   */
  const renderDescription = (description: string) => {
    let beforeNumber = description;
    let number = '';
    let afterNumber = '';

    const regexDescription = /(SUB|JOB|ACJ|ACT)-\d{6}-\d{6}/;
    const match = description.match(regexDescription);

    if (match) {
      const matchedText = match[0];
      const startIndex = description.indexOf(matchedText);
      const endIndex = startIndex + matchedText.length;
      beforeNumber = description.substring(0, startIndex);
      number = matchedText;
      afterNumber = description.substring(endIndex, description.length);
    }

    return {
      beforeNumber,
      number,
      afterNumber,
    };
  };

  return (
    <Layout>
      <AppBar
        position="fixed"
        sx={{
          width: `calc(100% - ${drawerWidth}px)`,
          ml: `${drawerWidth}px`,
          backgroundColor: 'white.light',
          borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
        }}
        elevation={0}
      >
        <Toolbar
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          <Grid>
            <Typography
              sx={{
                fontSize: '20px',
                lineHeight: 1,
                color: 'grey1.light',
              }}
            >
              Concierge / Activity Log
            </Typography>
          </Grid>
          <Grid display="flex" gap="8px" flexGrow={1} justifyContent="center">
            <InputBase
              placeholder="Search"
              sx={{
                maxWidth: '369px',
                height: '41px',
                flexGrow: 1,
                background: '#F2F4F6',
                border: '1px solid #D9D9D9',
                p: '0px 7px 0px 11px',
                borderRadius: '5px',
                [`& .${inputBaseClasses.input}`]: {
                  fontSize: 14,
                  fontWeight: 400,
                  lineHeight: '100%',
                  p: 0,
                  height: 'fit-content',
                },
              }}
              onChange={onChangeSearch}
              value={searchKeyword}
              endAdornment={
                <InputAdornment position="end">
                  <Magnifier />
                </InputAdornment>
              }
            />
          </Grid>
        </Toolbar>
      </AppBar>
      <Stack spacing="8px">
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            border: '1px solid #D9D9D9',
            maxHeight: 'calc(100vh - 150px)',
          }}
        >
          <Table
            stickyHeader
            sx={{ minWidth: 650 }}
            aria-labelledby="tableTitle"
          >
            <HeadTable<ACTIVITY_LOG_ITEM_TYPE>
              numSelected={selected.length}
              order={sortItem?.order}
              orderBy={sortItem?.orderBy}
              rowCount={list.records.length}
              headCells={headCells}
              onCheckbox={false}
              onAction={false}
              onRequestSort={handleRequestSort}
            />
            <TableBody
              sx={{
                height: 'auto !important',
              }}
            >
              {list.records.map((row, index) => {
                const isItemSelected = isSelected(row.id);
                const labelId = `enhanced-table-checkbox-${index}`;
                return (
                  <TableRow
                    hover
                    role="checkbox"
                    aria-checked={isItemSelected}
                    tabIndex={-1}
                    key={row.id}
                    selected={isItemSelected}
                  >
                    <TableCell
                      component="th"
                      scope="row"
                      align="left"
                      id={labelId}
                      sx={{
                        borderRight: '1px solid #DEE2E6',
                        padding: '18px 12px 17px',
                        fontSize: '14px',
                        fontWeight: 400,
                      }}
                    >
                      {row.number}
                    </TableCell>
                    <TableCell
                      component="th"
                      scope="row"
                      align="left"
                      sx={{
                        borderRight: '1px solid #DEE2E6',
                        padding: '18px 12px 17px',
                        fontSize: '14px',
                        fontWeight: 400,
                        textTransform: 'capitalize',
                      }}
                    >
                      {row.action}
                    </TableCell>
                    <TableCell
                      align="left"
                      sx={{
                        borderRight: '1px solid #DEE2E6',
                        padding: '18px 12px 17px',
                      }}
                    >
                      {row.created_by_role === ACTIVITY_LOG_ROLE.SYSTEM ? (
                        <Typography fontSize={14} fontWeight={400}>
                          System
                        </Typography>
                      ) : row.created_by_role === ACTIVITY_LOG_ROLE.ADMIN ? (
                        <Typography fontSize={14} fontWeight={400}>
                          {row.Admin?.full_name}&nbsp;
                          <span>{`(${row.Admin?.email})`}</span>
                        </Typography>
                      ) : (
                        <Typography fontSize={14} fontWeight={400}>
                          {row.User?.first_name + ' ' + row.User?.last_name}
                          &nbsp;
                          <span>{`(${row.User?.email})`}</span>
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell
                      align="left"
                      sx={{
                        padding: '18px 12px 17px',
                        borderRight: '1px solid #DEE2E6',
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: '14px',
                          fontWeight: 400,
                          textTransform: 'capitalize',
                        }}
                      >
                        {renderDescription(row.description).beforeNumber}
                        <span
                          onClick={() => redirectDescription(row)}
                          style={{ color: '#226FE3', cursor: 'pointer' }}
                        >
                          {renderDescription(row.description).number}
                        </span>
                        {renderDescription(row.description).afterNumber}
                      </Typography>
                    </TableCell>
                    <TableCell
                      align="left"
                      sx={{
                        borderRight: '1px solid #DEE2E6',
                        padding: '18px 12px 17px',
                        fontSize: '14px',
                        fontWeight: 400,
                        textTransform: 'capitalize',
                      }}
                    >
                      {row.created_by_role}
                    </TableCell>
                    <TableCell
                      align="left"
                      sx={{
                        borderRight: '1px solid #DEE2E6',
                        padding: '18px 12px 17px',
                        fontSize: '14px',
                        fontWeight: 400,
                      }}
                    >
                      {formatDateTime(row.createdAt)}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <TableCaption
          rowsPerPage={params?.limit ?? 0}
          handleChangeRowsPerPage={handleChangeRowsPerPage}
          metadata={list.metadata}
          items={items}
        />
      </Stack>
      {openDeletePopup && (
        <DeleteDialog
          type={DELETE_DIALOG_TYPE.ACTIVITY_LOGS}
          name={activityLogItem?.id}
          open={openDeletePopup}
          onClose={onClosePopup}
          subject={activityLogItem?.id}
          onSuccess={onReloadList}
        />
      )}
    </Layout>
  );
};

export default ActivityLogAC;
